import { motion } from "framer-motion";
import { useI18n } from "@/context/LanguageContext";
import { Star } from "./Star";

const roomLabelKey = (typeKey) =>
  ({ double: "dealRoomDouble", single: "dealRoomSingle", triple: "dealRoomTriple" }[typeKey]);

export const Hotels = () => {
  const { content, t } = useI18n();
  const h = t.hotels;
  return (
    <section id="allotjament" data-testid="hotels-section" className="bg-[#FFF7F1] py-16 md:py-36">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-12 gap-6 md:gap-12 mb-10 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <div className="font-condensed font-bold uppercase tracking-[0.25em] text-sm md:text-base text-[#F0311B]">{h.eyebrow}</div>
            <h2 className="mt-4 font-display font-bold text-[13vw] sm:text-6xl md:text-7xl leading-[0.9] text-[#141518]">
              {h.titleA} <span className="italic">{h.titleAccent}</span> <br />
              {h.titleB}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:pt-6">
            <p className="text-[#5A5A5F] text-base md:text-lg leading-relaxed">
              {h.intro}
              <strong className="text-[#141518]">{h.introBold}</strong>
              {h.introTail}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {content.hotels.map((it, i) => (
            <motion.div
              key={it.name}
              data-testid={`hotel-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="flex flex-col border-2 border-[#141518] bg-white shadow-hard"
            >
              <a
                href={it.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`hotel-card-link-${i}`}
                className="group block hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all duration-200"
              >
                <div className="img-card relative aspect-[4/5] overflow-hidden border-b-2 border-[#141518]">
                  <img src={it.img} alt={it.name} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3 bg-white text-[#141518] px-3 py-1 flex items-center gap-1 font-condensed text-[10px] uppercase tracking-[0.2em] border-2 border-[#141518]">
                    {Array.from({ length: it.stars }).map((_, k) => (
                      <Star key={k} size={10} className="text-[#F0311B]" />
                    ))}
                  </div>
                  {it.deal && (
                    <div className="absolute top-3 right-3 bg-[#F0311B] text-white px-3 py-1 font-condensed text-[10px] uppercase tracking-[0.2em] border-2 border-[#141518]">
                      ✱ {h.dealBadge}
                    </div>
                  )}
                </div>
                <div className="p-5 md:p-6 bg-white">
                  <div className="font-condensed uppercase tracking-[0.2em] text-[10px] text-[#5A5A5F]">{it.distance}</div>
                  <div className="mt-2 font-display font-bold text-2xl md:text-3xl leading-tight text-[#141518]">{it.name}</div>
                  <div className="mt-4 flex items-center justify-between font-condensed uppercase tracking-[0.2em] text-[10px] text-[#F0311B]">
                    <span>{h.bookCta}</span>
                    <span className="transition-transform group-hover:translate-x-1">→</span>
                  </div>
                </div>
              </a>

              {it.deal && (
                <div
                  data-testid={`hotel-deal-${i}`}
                  className="bg-[#FDECD7] border-t-2 border-[#141518] p-5 md:p-6"
                >
                  <div className="font-condensed uppercase tracking-[0.22em] text-[10px] text-[#F0311B]">
                    ★ {h.dealTitle}
                  </div>
                  <ul className="mt-3 divide-y divide-[#141518]/10">
                    {it.deal.rooms.map((r) => (
                      <li key={r.typeKey} className="flex items-baseline justify-between py-2">
                        <span className="font-condensed uppercase tracking-[0.15em] text-xs text-[#141518]">
                          {h[roomLabelKey(r.typeKey)]}
                        </span>
                        <span className="font-display font-bold text-lg text-[#141518] tabular-nums">
                          {r.price} <span className="text-[10px] text-[#5A5A5F] font-condensed tracking-normal normal-case">{r.tax} {h.dealTaxNote}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-3 border-t border-[#141518]/15 text-xs text-[#141518]/85 leading-relaxed">
                    {h.dealLateCheckout} <strong>{it.deal.lateCheckout}</strong> · {h.dealLateCheckoutFee} <strong>{it.deal.lateCheckoutFee}</strong> {h.dealLateCheckoutFeeSuffix}.
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
